import type { Crumb } from '../IndividualPlan/TopBar';
import type { GroupBy, GrowthFigures, GrowthPage, GrowthPeriod, GrowthRow, GrowthStudentRow, GrowthTile, SchemeLevel, Skill, SkillRow } from './types';
import { SKILL_LABELS, SKILLS } from './types';

export const GROWTH_ROUTE = '/dashboard/growth';

export function districtRoute(group?: GroupBy): string {
  return group === 'school' ? `${GROWTH_ROUTE}?group=school` : GROWTH_ROUTE;
}

export function gradeRoute(grade: string): string {
  return `${GROWTH_ROUTE}/grades/${encodeURIComponent(grade)}`;
}

export function schoolRoute(school: string, grade?: string): string {
  const base = `${GROWTH_ROUTE}/schools/${encodeURIComponent(school)}`;
  return grade ? `${base}?grade=${encodeURIComponent(grade)}` : base;
}

export function teacherRoute(teacher: string, school?: string): string {
  const base = `${GROWTH_ROUTE}/teachers/${encodeURIComponent(teacher)}`;
  return school ? `${base}?school=${encodeURIComponent(school)}` : base;
}

export function studentRoute(studentId: string): string {
  return `${GROWTH_ROUTE}/students/${encodeURIComponent(studentId)}`;
}

export const EN_DASH = '—';

export function bandOf(scheme: SchemeLevel[], value: number): number {
  const levels = [...scheme].sort((a, b) => a.score - b.score);
  if (levels.length === 0) return 0;
  let band = 1;
  for (let i = 1; i < levels.length; i += 1) {
    if (value >= levels[i - 1]!.score + 0.5) band = i + 1;
  }
  return band;
}

export function bandLabel(scheme: SchemeLevel[], band: number): string {
  const levels = [...scheme].sort((a, b) => a.score - b.score);
  return levels[band - 1]?.label ?? '';
}

export function bandColor(scheme: SchemeLevel[], band: number): string {
  const levels = [...scheme].sort((a, b) => a.score - b.score);
  return levels[band - 1]?.color ?? '';
}

export interface BandRange {
  band: number;
  label: string;
  color: string;
  range: string;
}

// The band edges bandOf uses: a band starts half a point above the level below it.
export function bandRanges(scheme: SchemeLevel[]): BandRange[] {
  const levels = [...scheme].sort((a, b) => a.score - b.score);
  return levels.map((level, i) => {
    const low = i === 0 ? level.score : levels[i - 1]!.score + 0.5;
    const high = i === levels.length - 1 ? level.score : level.score + 0.4;
    return { band: i + 1, label: level.label, color: level.color, range: `${low.toFixed(1)}–${high.toFixed(1)}` };
  });
}

export function named(value: number | null, scheme: SchemeLevel[]): string {
  if (value === null) return EN_DASH;
  return `${value.toFixed(1)} ${bandLabel(scheme, bandOf(scheme, value))}`.trim();
}

export function signed(value: number | null): string {
  if (value === null) return EN_DASH;
  const rounded = Math.round(value * 10) / 10;
  if (rounded > 0) return `+${rounded.toFixed(1)}`;
  if (rounded < 0) return `−${Math.abs(rounded).toFixed(1)}`;
  return '0.0';
}

export function pct(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}

export function levelsPercent(levels: number[]): number[] {
  const total = levels.reduce((a, b) => a + b, 0);
  if (total === 0) return levels.map(() => 0);
  const raw = levels.map((v) => (v / total) * 100);
  const floored = raw.map((v) => Math.floor(v));
  let remainder = 100 - floored.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || b.i - a.i);
  for (const { i } of order) {
    if (remainder <= 0) break;
    floored[i] = (floored[i] ?? 0) + 1;
    remainder -= 1;
  }
  return floored;
}

export interface Histogram {
  boy: number[];
  later: number[];
  belowBoy: number;
  aboveBoy: number;
  belowLater: number;
  aboveLater: number;
  total: number;
}

export function histogramOf(grid: number[][]): Histogram {
  const boyCounts = grid.map((row) => row.reduce((a, b) => a + b, 0));
  const laterCounts = [0, 1, 2, 3, 4].map((j) => grid.reduce((a, row) => a + (row[j] ?? 0), 0));
  const total = boyCounts.reduce((a, b) => a + b, 0);
  const upper = (counts: number[]) => counts.slice(2).reduce((a, b) => a + b, 0);
  const aboveBoy = pct(upper(boyCounts), total);
  const aboveLater = pct(upper(laterCounts), total);
  return {
    boy: levelsPercent(boyCounts),
    later: levelsPercent(laterCounts),
    belowBoy: total === 0 ? 0 : 100 - aboveBoy,
    aboveBoy,
    belowLater: total === 0 ? 0 : 100 - aboveLater,
    aboveLater,
    total,
  };
}

export function onTrackRate(f: GrowthFigures): number | null {
  return f.matched === 0 ? null : pct(f.onTrack, f.matched);
}

export function bridgingPlusRate(f: GrowthFigures): number | null {
  return f.matched === 0 ? null : pct(f.bridgingPlus, f.matched);
}

export function sortLeastGrowthFirst(rows: GrowthRow[], skill: Skill): GrowthRow[] {
  return [...rows].sort((a, b) => {
    const ra = onTrackRate(a.skills[skill]);
    const rb = onTrackRate(b.skills[skill]);
    if (ra === null && rb === null) return 0;
    if (ra === null) return 1;
    if (rb === null) return -1;
    return ra - rb;
  });
}

export function matchedText(f: GrowthFigures, open: boolean): string {
  return `${f.matched} of ${f.students} ${open ? 'so far' : 'matched'}`;
}

export interface ReturnTestingParts {
  percent: number;
  main: string;
  parts: { label: string; value: number }[];
}

export function returnTestingParts(f: GrowthFigures, later: string, here: boolean, open: boolean): ReturnTestingParts {
  const r = f.returnTesting;
  return {
    percent: pct(r.matched, r.total),
    main: `${r.matched} of ${r.total} tested at BOY and ${later}${open ? ' so far' : ''}`,
    parts: [
      { label: here ? 'No BOY here' : 'No BOY score', value: r.noBoy },
      { label: `No ${later} score${open ? ' yet' : ''}`, value: r.noLater },
      { label: 'Unscorable', value: r.unscorable },
    ],
  };
}

export function laterLabel(period: GrowthPeriod | null): string {
  return period?.later === 'eoy' ? 'EOY' : 'MOY';
}

export function growthWord(period: GrowthPeriod | null): string {
  return period?.later === 'eoy' ? 'expected growth or better' : 'on track';
}

export function csvCell(value: string): string {
  return /[",\n\r\t]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function csvName(value: string): string {
  return /^[=+\-@\t\r\n]/.test(value) ? `'${value}` : value;
}

export function formatDataAsOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `Data as of ${day}, ${time}`;
}

export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map((v) => Number(v));
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '';
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

export interface TileCopy {
  empty: boolean;
  periodLine: string;
  open: boolean;
  headline: { number: number; line: string; of: string };
  skills: { label: string; percent: number; word: string }[];
  foot: string;
}

export function tileCopy(tile: GrowthTile): TileCopy {
  const period = tile.period;
  if (!period) {
    return {
      empty: true,
      periodLine: '',
      open: false,
      headline: { number: 0, line: '', of: '' },
      skills: [],
      foot: tile.opensAt ? `${(tile.opensWindow ?? 'boy').toUpperCase()} opens ${formatDay(tile.opensAt)}` : 'No benchmark results yet',
    };
  }
  const later = laterLabel(period);
  const word = growthWord(period);
  const line = period.later === 'eoy' ? `students with ${word} in at least one skill` : `students ${word} in at least one skill`;
  let foot = formatDataAsOf(tile.dataAsOf);
  if (period.open) foot = `${later} open · ${tile.tested} of ${tile.students} tested`;
  else if (period.lastYear) foot = `This year starts after MOY${tile.nextStartsAt ? ` · ${formatDay(tile.nextStartsAt)}` : ''}`;
  return {
    empty: false,
    periodLine: `${period.label}, BOY → ${later}`,
    open: period.open,
    headline: {
      number: tile.headline.onTrack,
      line,
      of: `of ${tile.headline.matched} who took both BOY and ${later}${period.open ? ' so far' : ''}`,
    },
    skills: SKILLS.map((skill) => {
      const entry = tile.skills[skill] ?? { matched: 0, onTrack: 0 };
      return { label: SKILL_LABELS[skill], percent: pct(entry.onTrack, entry.matched), word };
    }),
    foot,
  };
}

export interface Trail {
  year?: number;
  level: 'district' | 'grade' | 'school' | 'teacher' | 'student';
  title?: string;
  grade?: string;
  school?: string;
  teacher?: string;
  viaGrade?: string;
  group?: GroupBy;
}

export function breadcrumbsFor(trail: Trail): Crumb[] {
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/' }];
  const link = (label: string, to: string, extra: Record<string, unknown> = {}): Crumb => {
    const state = { ...(trail.year === undefined ? {} : { year: trail.year }), ...extra };
    return Object.keys(state).length === 0 ? { label, to } : { label, to, state };
  };
  const grouped = trail.group === undefined ? {} : { group: trail.group };
  if (trail.level === 'district') {
    crumbs.push({ label: 'Flashlight360 results' });
    return crumbs;
  }
  crumbs.push(link('Flashlight360 results', districtRoute(trail.group)));
  if (trail.level === 'grade') {
    crumbs.push({ label: trail.title ?? `Grade ${trail.grade ?? ''}` });
    return crumbs;
  }
  if (trail.viaGrade) crumbs.push(link(`Grade ${trail.viaGrade}`, gradeRoute(trail.viaGrade)));
  if (trail.level === 'school') {
    crumbs.push({ label: trail.title ?? trail.school ?? '' });
    return crumbs;
  }
  if (trail.school) crumbs.push(link(trail.school, schoolRoute(trail.school, trail.viaGrade), grouped));
  if (trail.level === 'teacher') {
    crumbs.push({ label: trail.title ?? trail.teacher ?? '' });
    return crumbs;
  }
  if (trail.teacher) crumbs.push(link(trail.teacher, teacherRoute(trail.teacher, trail.school), { ...grouped, ...(trail.viaGrade === undefined ? {} : { viaGrade: trail.viaGrade }) }));
  crumbs.push({ label: trail.title ?? '' });
  return crumbs;
}

function rateCell(rate: number | null): string {
  return rate === null ? '' : `${rate}%`;
}

export function buildGroupCsv(rows: GrowthRow[], skill: Skill, firstColumn: string, growthLabel: string, options: { averages: boolean } = { averages: true }): string {
  const averages = (cells: string[]) => (options.averages ? cells : []);
  const lines = [[firstColumn, 'Students', 'Matched', ...averages(['Avg', 'BOY avg']), 'Growth', growthLabel, 'Bridging+ at BOY'].map(csvCell).join(',')];
  for (const row of rows) {
    const f = row.skills[skill];
    lines.push(
      [
        csvName(row.label),
        String(row.students),
        String(f.matched),
        ...averages([f.avg === null ? '' : f.avg.toFixed(1), f.boyAvg === null ? '' : f.boyAvg.toFixed(1)]),
        f.growth === null ? '' : signed(f.growth),
        rateCell(onTrackRate(f)),
        rateCell(bridgingPlusRate(f)),
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return lines.join('\n');
}

function cellLabel(row: SkillRow, window: 'boy' | 'moy' | 'eoy'): string {
  const cell = row[window];
  if (cell.state === 'unscorable') return 'Unscorable';
  if (cell.state === 'submitted') return 'Submitted';
  return cell.label ?? '';
}

export function buildTeacherCsv(page: GrowthPage, rows: GrowthStudentRow[] = page.studentRows): string {
  const later = laterLabel(page.period);
  const header = ['Student', 'Student ID'];
  for (const skill of SKILLS) {
    const label = SKILL_LABELS[skill];
    header.push(`${label} BOY`, `${label} MOY`, `${label} EOY`, `${label} change`);
  }
  header.push(`${later} status`);
  const lines = [header.map(csvCell).join(',')];
  for (const student of rows) {
    const cells = [csvName(student.name), csvName(student.id)];
    for (const skill of SKILLS) {
      const row = student[skill];
      cells.push(cellLabel(row, 'boy'), cellLabel(row, 'moy'), cellLabel(row, 'eoy'), row.change === null ? '' : signed(row.change));
    }
    cells.push(student.status);
    lines.push(cells.map(csvCell).join(','));
  }
  return lines.join('\n');
}
